import { useEffect, useState } from "react";

import {
  resolveAdvertisedAgentRuntime,
  applianceAgentRuntimeBaseUrl,
  skillIdsFromAgentCard,
} from "../services/studio-agent-runtime-a2a";

interface BotRosterStripProps {
  /** Lowercase DNS-style slug of the bot this thread talks to. */
  applicationId: string;
}

type RosterState =
  | { status: "loading" }
  | { status: "ready"; applicationId: string; skillIds: string[] }
  | { status: "unavailable" };

/**
 * The bot roster for the editor chat.
 *
 * What a bot can do is DERIVED FROM ITS AGENT CARD, never from a list kept
 * here: the gateway only advertises skills it can actually execute (G1), so
 * the card is the single honest source. A skill that stops being advertised
 * stops having a chip, with no change in this file.
 *
 * Failure is shown as failure. An unreadable card (401/500) and an
 * advertised-but-EMPTY roster are both "unavailable" -- per the consumer
 * protocol an empty roster is refusal evidence, not a working bot, and
 * drawing it as a healthy chip would be the exact mis-signal the contract
 * warns about.
 */
export function BotRosterStrip({ applicationId }: BotRosterStripProps) {
  const [state, setState] = useState<RosterState>({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ status: "loading" });
    void (async () => {
      // Resolve, do not assume. The id handed in is derived from the project
      // SLUG, and a well-formed slug is not evidence that a bot lives there:
      // measured live, an unpublished project's slug answered 502 and the
      // strip reported "연결 대기", asserting a connection that was never
      // coming. `resolveAdvertisedAgentRuntime` tries that id and falls back
      // to the resident bot, so the strip shows what actually answers.
      let resolved: Awaited<ReturnType<typeof resolveAdvertisedAgentRuntime>> = null;
      try {
        resolved = await resolveAdvertisedAgentRuntime(
          window.location.origin,
          applicationId,
        );
      } catch {
        resolved = null;
      }
      if (cancelled) return;
      const skillIds = skillIdsFromAgentCard(resolved?.card);
      setState(
        skillIds.length > 0
          ? { status: "ready", applicationId: resolved!.applicationId, skillIds }
          : { status: "unavailable" },
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [applicationId]);

  if (state.status === "loading") return null;

  if (state.status === "unavailable") {
    return (
      <div
        data-testid="bot-roster-unavailable"
        className="px-4 pb-1 text-[11px] text-muted-foreground"
      >
        <span className="rounded-full border border-destructive/40 bg-destructive/5 px-2 py-0.5 text-destructive">
          {applicationId} · 연결 대기
        </span>
      </div>
    );
  }

  return (
    <div
      data-testid="bot-roster-strip"
      className="flex flex-wrap items-center gap-1 px-4 pb-1"
    >
      {/* The bot that ANSWERED, not the one that was asked for. Listing the
          resident bot's skills under a project slug attributes another bot's
          capabilities to a project that has none (seen live once the fallback
          landed). */}
      <span className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-medium">
        {state.applicationId}
      </span>
      {state.skillIds.map((skillId) => (
        <span
          key={skillId}
          data-testid={`bot-skill-badge-${skillId}`}
          className="rounded-full border border-border bg-background px-2 py-0.5 text-[10px] text-muted-foreground"
        >
          {skillId}
        </span>
      ))}
    </div>
  );
}
